import { useState, useCallback } from 'react';

/**
 * useState wrapper that persists value to localStorage.
 * Falls back to defaultValue if localStorage is unavailable or value is invalid.
 */
export function usePersistedState<T extends string>(
  key: string,
  defaultValue: T,
  validValues?: readonly T[],
): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      if (validValues && !validValues.includes(stored as T)) return defaultValue;
      return stored as T;
    } catch {
      return defaultValue;
    }
  });

  const setPersistedState = useCallback(
    (value: T) => {
      setState(value);
      try {
        if (value === defaultValue) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, value);
        }
      } catch {
        // localStorage full or unavailable — ignore
      }
    },
    [key, defaultValue],
  );

  return [state, setPersistedState];
}
